// src/pages/ExamsList.jsx — v4d Phase 2 : max_attempts
import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Stack, TextField, InputAdornment, Collapse,
  Divider, Button, Chip, CircularProgress, Tooltip, alpha,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import PlayArrowIcon     from "@mui/icons-material/PlayArrow";
import MenuBookIcon      from "@mui/icons-material/MenuBook";
import AccessTimeIcon    from "@mui/icons-material/AccessTime";
import EventIcon         from "@mui/icons-material/Event";
import QuizIcon          from "@mui/icons-material/Quiz";
import EmojiEventsIcon   from "@mui/icons-material/EmojiEvents";
import BlockIcon         from "@mui/icons-material/Block";
import RepeatIcon        from "@mui/icons-material/Repeat";
import API from "../api";

const P = {
  navy:   "#1e3a5f",
  navyL:  "#243b5c",
  red:    "#c0392b",
  redL:   "#e74c3c",
  teal:   "#0d9488",
  amber:  "#d97706",
  slate:  { 50:"#f8fafc", 100:"#f1f5f9", 200:"#e2e8f0", 300:"#cbd5e1", 400:"#94a3b8",
             500:"#64748b", 600:"#475569", 700:"#334155", 800:"#1e293b" },
  white:  "#ffffff",
  indigo: { 50:"#eef2ff", 500:"#6366f1", 600:"#4f46e5" },
};

// ── ExamRow ───────────────────────────────────────────────────────
function ExamRow({ exam, index, isOpen, onToggle }) {
  const navigate = useNavigate();
  const qc = exam.question_count ?? "?";

  // Compteur de tentatives chargé à l'ouverture de l'accordéon
  const [attInfo,  setAttInfo]  = useState(null);  // { used, max, remaining, blocked }
  const [attLoad,  setAttLoad]  = useState(false);

  useEffect(() => {
    if (!isOpen || attInfo !== null) return;
    setAttLoad(true);
    API.get(`/attempts/count/${exam.id}`)
      .then(r => setAttInfo(r.data))
      .catch(() => setAttInfo({ used:0, max:null, remaining:null, blocked:false }))
      .finally(() => setAttLoad(false));
  }, [isOpen, exam.id, attInfo]);

  const blocked   = attInfo?.blocked ?? false;
  const maxAtt    = attInfo?.max ?? null;
  const remaining = attInfo?.remaining ?? null;
  const used      = attInfo?.used ?? 0;

  return (
    <Box sx={{ borderBottom:`1px solid ${P.slate[200]}` }}>
      {/* Header row */}
      <Box onClick={onToggle} sx={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        px:3, py:2.5, cursor:"pointer", background: isOpen ? P.slate[50] : P.white,
        transition:"background .15s",
        "&:hover": { background: P.slate[50] },
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{
            fontWeight:700, color: P.slate[800], fontSize:"0.95rem",
            minWidth:22, display:"inline-block",
          }}>
            {index + 1}.
          </Typography>
          <Typography sx={{ fontWeight:600, color: P.slate[800], fontSize:"0.95rem" }}>
            {exam.title}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {exam.duration_minutes && (
            <Chip icon={<AccessTimeIcon sx={{ fontSize:"0.75rem !important" }} />}
              label={`${exam.duration_minutes} min`} size="small"
              sx={{ background:alpha(P.navy,.07), color:P.navy, fontWeight:600, fontSize:"0.7rem", height:22 }} />
          )}
          {qc !== "?" && (
            <Chip icon={<QuizIcon sx={{ fontSize:"0.75rem !important" }} />}
              label={`${qc} Q`} size="small"
              sx={{ background:alpha(P.teal,.1), color:P.teal, fontWeight:600, fontSize:"0.7rem", height:22 }} />
          )}
          {isOpen
            ? <ExpandLessIcon sx={{ color: P.slate[400], fontSize:20 }} />
            : <ExpandMoreIcon sx={{ color: P.slate[400], fontSize:20 }} />
          }
        </Stack>
      </Box>

      {/* Expanded actions */}
      <Collapse in={isOpen}>
        <Box sx={{ background: P.slate[50], px:3, py:2.5 }}>

          {/* Compteur de tentatives */}
          {attLoad ? (
            <Box sx={{ display:"flex", justifyContent:"center", mb:2 }}>
              <CircularProgress size={18} sx={{ color:P.navy }} />
            </Box>
          ) : maxAtt !== null && (
            <Box sx={{
              mb:2, px:2, py:1.25, borderRadius:"8px",
              background: blocked ? alpha("#ef4444",.07) : alpha(P.amber,.07),
              border:`1px solid ${blocked ? alpha("#ef4444",.25) : alpha(P.amber,.25)}`,
              display:"flex", alignItems:"center", justifyContent:"space-between", gap:1,
            }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RepeatIcon sx={{ fontSize:16, color: blocked ? "#ef4444" : P.amber }} />
                <Typography variant="body2" sx={{ fontWeight:600, color: blocked ? "#ef4444" : P.amber }}>
                  {blocked
                    ? `Limite atteinte — ${used}/${maxAtt} tentative${maxAtt>1?"s":""} utilisée${maxAtt>1?"s":""}`
                    : `${used}/${maxAtt} tentative${maxAtt>1?"s":""} · ${remaining} restante${remaining>1?"s":""}`
                  }
                </Typography>
              </Stack>
              {/* Barre de progression */}
              <Box sx={{ width:80, height:5, borderRadius:3, background:alpha(P.slate[300],.5), flexShrink:0 }}>
                <Box sx={{
                  height:"100%", borderRadius:3,
                  background: blocked ? "#ef4444" : P.amber,
                  width:`${Math.min((used/maxAtt)*100,100)}%`,
                  transition:"width .3s",
                }} />
              </Box>
            </Box>
          )}

          {/* Fenêtre de passage */}
          {(exam.available_from || exam.available_until) && (() => {
            const now    = new Date();
            const from   = exam.available_from  ? new Date(exam.available_from)  : null;
            const until  = exam.available_until ? new Date(exam.available_until) : null;
            const closed = until && now > until;
            const notYet = from  && now < from;
            return (
              <Box sx={{ mb:2, px:2, py:1.25, borderRadius:"8px",
                background: closed||notYet ? alpha("#ef4444",.07) : alpha(P.teal,.07),
                border:`1px solid ${closed||notYet ? alpha("#ef4444",.25) : alpha(P.teal,.3)}`,
                display:"flex", alignItems:"center", gap:1 }}>
                <EventIcon sx={{ fontSize:15, color: closed||notYet ? "#ef4444" : P.teal }} />
                <Typography variant="body2" sx={{ fontSize:"0.78rem", fontWeight:600,
                  color: closed||notYet ? "#ef4444" : P.teal }}>
                  {closed  ? `Fermé depuis le ${until.toLocaleDateString("fr-FR")}` :
                   notYet  ? `Disponible le ${from.toLocaleDateString("fr-FR")}` :
                   from && until ? `Disponible du ${from.toLocaleDateString("fr-FR")} au ${until.toLocaleDateString("fr-FR")}` :
                   until ? `Fermeture le ${until.toLocaleDateString("fr-FR")}` :
                   `Ouverture le ${from.toLocaleDateString("fr-FR")}`}
                </Typography>
              </Box>
            );
          })()}

          <Stack direction={{ xs:"column", sm:"row" }} spacing={1.5} justifyContent="center" alignItems="center">
            {/* Mode apprentissage — toujours disponible */}
            <Button
              variant="outlined"
              startIcon={<MenuBookIcon />}
              endIcon={<PlayArrowIcon sx={{ fontSize:"0.9rem !important" }} />}
              onClick={() => navigate(`/exams/${exam.id}?mode=learn`)}
              sx={{
                minWidth:200, borderColor:P.red, color:P.red, fontWeight:700,
                borderRadius:"8px", textTransform:"none", fontSize:"0.9rem",
                "&:hover": { background:alpha(P.red,.06), borderColor:P.redL },
              }}>
              Start Learn Test
            </Button>

            {/* Mode test complet — bloqué si limite atteinte */}
            <Tooltip
              title={blocked ? `Vous avez atteint la limite de ${maxAtt} tentative${maxAtt>1?"s":""} pour cet examen.` : ""}
              disableHoverListener={!blocked}
            >
              <span style={{ display:"inline-flex" }}>
                <Button
                  variant="outlined"
                  startIcon={blocked ? <BlockIcon /> : <EmojiEventsIcon />}
                  endIcon={!blocked && <PlayArrowIcon sx={{ fontSize:"0.9rem !important" }} />}
                  disabled={blocked}
                  onClick={() => !blocked && navigate(`/exams/${exam.id}`)}
                  sx={{
                    minWidth:200,
                    borderColor: blocked ? P.slate[300] : P.red,
                    color: blocked ? P.slate[400] : P.red,
                    fontWeight:700, borderRadius:"8px", textTransform:"none", fontSize:"0.9rem",
                    "&:hover": blocked ? {} : { background:alpha(P.red,.06), borderColor:P.redL },
                    "&.Mui-disabled": { borderColor:P.slate[300], color:P.slate[400] },
                  }}>
                  {blocked ? "Limite atteinte" : "Start Full Test"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Page principale ───────────────────────────────────────────────
export default function ExamsList() {
  const { t } = useTranslation();
  const [exams,   setExams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [openId,  setOpenId]  = useState(null);

  const load = useCallback(async (p = 1, s = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: 20 });
      if (s) params.set("search", s);
      const { data } = await API.get(`/exams/?${params}`);
      const items = data.items || [];
      setExams(p === 1 ? items : prev => [...prev, ...items]);
      setTotal(data.total || 0);
      setHasMore(data.page < data.pages);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { setPage(1); load(1, search); }, [search, load]);

  const filtered = search
    ? exams.filter(e => e.title.toLowerCase().includes(search.toLowerCase()))
    : exams;

  return (
    <Box sx={{ maxWidth: 860, mx:"auto" }}>

      {/* Page header */}
      <Box sx={{
        background: P.navy, color:"white",
        px:{ xs:3, md:5 }, py:{ xs:3, md:4 },
        borderRadius:"16px", mb:3,
      }}>
        <Typography sx={{ fontSize:"1.5rem", fontWeight:800, letterSpacing:"-0.01em", mb:.5 }}>
          Certification Practice Tests &amp; Study Materials
        </Typography>
        <Typography sx={{ color:"rgba(255,255,255,.7)", fontSize:"0.9rem", lineHeight:1.6 }}>
          Prepare with real exam-style questions. Choose Learn mode for step-by-step guidance,
          or Full Test to simulate exam conditions.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt:2 }}>
          {[
            { v: total, l:"Tests disponibles" },
            { v:"∞",    l:t("exams.questions") },
            { v:"2",    l:"Modes" },
          ].map(s => (
            <Box key={s.l} sx={{ textAlign:"center", background:"rgba(255,255,255,.08)", px:2.5, py:1.5, borderRadius:2 }}>
              <Typography sx={{ fontWeight:800, fontSize:"1.2rem", lineHeight:1 }}>{s.v}</Typography>
              <Typography sx={{ fontSize:"0.68rem", color:"rgba(255,255,255,.6)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{s.l}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Search */}
      <Box sx={{ mb:2 }}>
        <TextField
          placeholder="Search a test name…"
          value={search}
          onChange={e => { setSearch(e.target.value); setOpenId(null); }}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              background:"white", borderRadius:"10px", fontSize:"0.95rem",
              "& fieldset": { borderColor: P.slate[300] },
              "&:hover fieldset": { borderColor: P.slate[400] },
              "&.Mui-focused fieldset": { borderColor: P.navy },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: P.slate[400] }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Accordion list */}
      {loading && page === 1 ? (
        <Box sx={{ py:6, textAlign:"center" }}>
          <CircularProgress sx={{ color: P.navy }} />
        </Box>
      ) : (
        <Box sx={{ background:"white", borderRadius:"12px", border:`1px solid ${P.slate[200]}`, overflow:"hidden", boxShadow:"0 1px 8px rgba(0,0,0,.06)" }}>
          <Box sx={{ background: P.slate[100], px:3, py:1.5, borderBottom:`1px solid ${P.slate[200]}` }}>
            <Typography sx={{ color: P.red, fontWeight:700, fontSize:"0.92rem" }}>
              {total} Practice Test{total !== 1 ? "s" : ""}
            </Typography>
          </Box>

          {filtered.length === 0 ? (
            <Box sx={{ py:8, textAlign:"center" }}>
              <QuizIcon sx={{ fontSize:48, color: P.slate[300], mb:1.5 }} />
              <Typography color="text.secondary">No tests found.</Typography>
            </Box>
          ) : (
            filtered.map((ex, i) => (
              <ExamRow
                key={ex.id}
                exam={ex}
                index={i}
                isOpen={openId === ex.id}
                onToggle={() => setOpenId(openId === ex.id ? null : ex.id)}
              />
            ))
          )}
        </Box>
      )}

      {hasMore && (
        <Box sx={{ textAlign:"center", mt:3 }}>
          <Button variant="outlined"
            onClick={() => { const n = page+1; setPage(n); load(n, search); }}
            disabled={loading}
            sx={{ borderColor:P.navy, color:P.navy, borderRadius:"8px", textTransform:"none" }}>
            {loading ? "Chargement…" : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
